/**
 * Re-sends a GraphQL request the server treated as signed out, over XHR.
 *
 * On some managed devices something replaces `window.fetch` and Apollo's requests leave
 * without the session cookie: the server answers `forbidden` while every XHR on the same
 * page is still signed in. If a REST session check says the cookie is live, the same
 * operation goes out again over the REST layer's XHR agent.
 * https://github.com/albert-io/nexus/issues/22709
 */
import {ApolloLink, Observable} from '@apollo/client';
import type {FetchResult, Operation} from '@apollo/client';
import throttle from 'lodash/throttle';

import {sessionStillValid} from 'lib/sessionStillValid';

import {sendOverXhr} from './sendOverXhr';
import {isSignedOutResult} from './signedOutResult';

// Calls inside the window get the leading call's promise, so one probe serves every refusal.
const sessionLive = throttle(sessionStillValid, 30_000, {trailing: false});

/** The answer sent again over XHR, or the refusal itself once the session is gone. */
function resend(
  operation: Operation,
  refusal: FetchResult,
  controller: AbortController
): Observable<FetchResult> {
  return new Observable<FetchResult>((observer) => {
    const deliver = (answer: FetchResult) => {
      observer.next(answer);
      observer.complete();
    };
    sessionLive()
      .then((live) =>
        live && !controller.signal.aborted ? sendOverXhr(operation, controller.signal) : null
      )
      .then(
        (answer) => deliver(answer ?? refusal),
        () => deliver(refusal)
      );
    return () => controller.abort();
  });
}

export const sessionFallbackLink = new ApolloLink((operation, forward) => {
  const controller = new AbortController();
  const requested: unknown = operation.getContext().fetchOptions?.signal;
  if (requested instanceof AbortSignal) {
    requested.addEventListener('abort', () => controller.abort(), {once: true});
  }
  return forward(operation).flatMap((result) =>
    isSignedOutResult(result) ? resend(operation, result, controller) : Observable.of(result)
  );
});

export const __testOnly = {forgetSession: () => sessionLive.cancel()};
